import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { School as SchoolIcon, WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";

// ─── Small formatting/sanitizing helpers ───────────────────────────────────
const sanitizeName = (name) =>
  (name ?? "")
    .replace(/[`'"]/g, "") // strip stray backticks/quotes from bad DB entries
    .trim();

// Normalizes any value used as a lookup key (examType / subExam names).
// Different endpoints represent "no sub-exam" inconsistently — GetExamResults
// uses subExam: "", while GetExamSubCategory_Result returns subExam: "--"
// for the same case. Unifying these avoids duplicate/blank columns.
const normKey = (val) => {
  const s = (val ?? "").toString().trim();
  return s === "--" || s === "-" ? "" : s;
};

const formatPercent = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(val)) return "N/A";
  return `${Number(val).toFixed(2)}%`;
};

const formatMarks = (val) => {
  if (val === null || val === undefined || val === "") return "--";
  const num = Number(val);
  return isNaN(num) ? "--" : (Number.isInteger(num) ? String(num) : num.toFixed(2));
};

// Color used for grade text/badges throughout the PDF
const gradeColor = (grade) => {
  switch ((grade || "").toString().trim().toUpperCase()) {
    case "A+":
    case "A":
      return [22, 130, 60]; // green
    case "B+":
    case "B":
      return [24, 100, 140]; // blue-teal
    case "C+":
    case "C":
      return [184, 134, 11]; // amber
    case "D":
      return [204, 102, 0]; // orange
    case "E":
    case "F":
      return [178, 34, 34]; // red
    default:
      return [60, 60, 60]; // neutral gray
  }
};

// Maps API monthId (1-12) to a display name — used only in the due-amount modal.
const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDueDate = (val) => {
  if (!val) return "--";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString();
};

const Result = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [resultData, setResultData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [studentProfile, setStudentProfile] = useState(null);
  const [studentAttendance, setStudentAttendance] = useState(null);

  // ─── Fee-due restriction state ─────────────────────────────────────────
  // Unlike the staff/admin panel, parents CANNOT bypass this — if dues are
  // pending, the result simply cannot be generated. There is no "Print
  // Anyways" option here on purpose.
  const [isFeeCheckLoading, setIsFeeCheckLoading] = useState(false);
  const [dueDialogOpen, setDueDialogOpen] = useState(false);
  const [dueData, setDueData] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const classId = sessionStorage.getItem("classId");
  const sectionId = sessionStorage.getItem("sectionId");

  // Fetch financial years (sessions)
  const fetchSessions = async () => {
    try {
      setLoadingBarProgress(30);
      const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data);

      // Determine the current financial year
      const today = new Date();
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const financialYearStart = new Date(currentYear, 3, 1); // April 1
      const financialYearEnd = new Date(nextYear, 2, 31); // March 31

      let currentSessionString;
      if (today >= financialYearStart && today <= financialYearEnd) {
        currentSessionString = `${currentYear}-${nextYear}`;
      } else {
        currentSessionString = `${currentYear - 1}-${currentYear}`;
      }

      // Find the matching session
      const currentSession = data.find(
        (item) => item.finanacialYear === currentSessionString
      );

      // Auto-select the current session
      if (currentSession) {
        setSession(currentSession.financialYearID);
      } else if (data.length > 0) {
        // Fallback: select the first session if current not found
        setSession(data[0].financialYearID);
      }

      setLoadingBarProgress(100);
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  // Fetch school logo
  const fetchSchoolLogo = async () => {
    try {
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch school logo");
      }

      const apiData = await response.json();
      const logoImg = apiData?.[0]?.logoImg;
      if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
        let cleanedBase64 = logoImg
          .replace(/^data:image\/[a-zA-Z]+;base64,/, "")
          .replace(/\s/g, "")
          .replace(/\n/g, "");
        const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
        cleanedBase64 += "=".repeat(paddingLength);
        setLogoDataUrl(`data:image/png;base64,${cleanedBase64}`);
      }
    } catch (error) {
      console.error("Error fetching school logo:", error);
    }
  };

  // Fetch result data
  const fetchResultData = async () => {
    if (!session || !classId || !sectionId) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/Result/GetStudentResultsGenerated`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          sessionId: session,
          examTypeId: 0,
          classId,
          sectionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const studentRollNo = sessionStorage.getItem("rollNo");

      if (Array.isArray(data) && data.length > 0) {
        const studentResult = data.find(
          (student) => student.rollNo === studentRollNo
        );

        setResultData(studentResult ? [studentResult] : []);
      } else {
        setSnackbarMessage("No result data available.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error fetching result data.");
      setOpenSnackbar(true);
      console.error("Error fetching result data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── FEE CHECK — runs before result generation, hard block on pending dues ──
  // Hits StudentResultWithFeeCheck first.
  //   - canViewResult: true  → dues are clear, proceed to PDF generation.
  //   - canViewResult: false → dues are pending. Show the breakdown and STOP —
  //     there is no bypass on the parent panel.
  const handlePrintClick = async (studentId) => {
    if (!session) {
      setSnackbarMessage("Missing session — please reload and try again.");
      setOpenSnackbar(true);
      return;
    }
    setIsFeeCheckLoading(true);
    try {
      const response = await fetch(`${apiUrl}/Result/StudentResultWithFeeCheck`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data.canViewResult) {
        fetchStudentProfile(studentId);
      } else {
        setDueData(data);
        setDueDialogOpen(true);
      }
    } catch (error) {
      console.error("Error checking fee status:", error);
      setSnackbarMessage("Error checking fee status. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setIsFeeCheckLoading(false);
    }
  };

  const handleCloseDueDialog = () => {
    setDueDialogOpen(false);
    setDueData(null);
  };

  // ─── Gather all data needed for the PDF ─────────────────────────────────
  // Same data-correctness fixes as the staff panel:
  //   - normKey() unifies the "--"/"" sub-exam placeholder mismatch between
  //     GetExamResults and GetExamSubCategory_Result.
  //   - The "ghost" subject row (subjectId: 0) is filtered out everywhere.
  //   - Subject names are sanitized.
  //   - Exam-wise totals are computed from `results` (has examTypeId on
  //     every row), not from `totalMarks`.
  //   - A real overall Pass/Fail status is computed (any subject grade E/F ⇒ FAIL).
  const fetchStudentProfile = async (studentId) => {
    try {
      setIsPdfLoading(true);
      setLoadingBarProgress(30);

      const profileResponse = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          yearId: session,
          studentId,
        }),
      });

      if (!profileResponse.ok) {
        throw new Error("Failed to fetch student profile");
      }

      const profileData = await profileResponse.json();
      const profile = profileData.objData;
      const attendance = profileData.totalAttendancList?.[0] ?? null;
      setStudentProfile(profile);
      setStudentAttendance(attendance);

      const resultsResponse = await fetch(`${apiUrl}/Result/GetExamResults`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });

      if (!resultsResponse.ok) {
        setLoadingBarProgress(0);
        throw new Error("Failed to fetch exam results");
      }
      setLoadingBarProgress(70);
      const resultsData = await resultsResponse.json();
      const results = resultsData.results ?? [];

      // Drop the "ghost" per-exam-type total row (subjectId 0 / blank name)
      // and sanitize real subject names.
      const subjectTotals = (resultsData.totalMarks1 ?? [])
        .filter((s) => s.subjectName && sanitizeName(s.subjectName) !== "")
        .map((s) => ({ ...s, subjectName: sanitizeName(s.subjectName) }));

      const grossTotal = resultsData.grosstotalMarks?.[0] ?? {};
      const overallGrade = grossTotal.totalGrade ?? "N/A";
      const totalPercentage =
        grossTotal.overallPercentage ?? grossTotal.averagePercentage ?? "N/A";

      const teacherRemarks = results[0]?.teacherRemarks ?? "N/A";
      const subjectNames = subjectTotals.map((s) => s.subjectName);

      const hasFailingGrade = subjectTotals.some((s) =>
        ["E", "F"].includes((s.totalGrade || "").toString().trim().toUpperCase())
      );
      const resultStatus = hasFailingGrade ? "FAIL" : "PASS";

      const examWiseTotals = Object.values(
        results.reduce((acc, r) => {
          if (r.subjectId === 0) return acc;
          const key = r.examTypeId;
          if (!acc[key]) acc[key] = { examType: normKey(r.examType), obtained: 0, max: 0 };
          acc[key].obtained += Number(r.obtainedMarks) || 0;
          acc[key].max += Number(r.maxMarks) || 0;
          return acc;
        }, {})
      ).map((e) => ({ ...e, percentage: e.max ? +((e.obtained / e.max) * 100).toFixed(2) : 0 }));

      const examCriteriaResponse = await fetch(
        `${apiUrl}/Result/GetExamCriteria_result`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ studentId }),
        }
      );

      if (!examCriteriaResponse.ok) {
        console.error("Failed to fetch exam criteria");
        setIsPdfLoading(false);
        return;
      }

      const examCriteriaData = await examCriteriaResponse.json();

      const subCategoryPromises = examCriteriaData.map(async (exam) => {
        const subCategoryResponse = await fetch(
          `${apiUrl}/Result/GetExamSubCategory_Result`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              studentId,
              examTypeId: exam.examCategoryId,
            }),
          }
        );

        if (!subCategoryResponse.ok) {
          console.error(`Failed to fetch sub-category for exam type ${exam.examType}`);
          return {
            examType: normKey(exam.examType),
            examCategoryId: exam.examCategoryId,
            subCategories: [],
          };
        }

        const subCategoryData = await subCategoryResponse.json();

        const subCategories = subCategoryData.map((sub) => {
          const subExamName = normKey(sub.subExam);
          const matchingResult = results.find(
            (r) => r.examTypeId === exam.examCategoryId && normKey(r.subExam) === subExamName
          );
          return {
            name: subExamName,
            maxMarks: matchingResult?.maxMarks ?? sub.maxMarks ?? "",
            minMarks: matchingResult?.minMarks ?? sub.minMarks ?? "",
          };
        });

        return {
          examType: normKey(exam.examType),
          examCategoryId: exam.examCategoryId,
          subCategories,
        };
      });

      const examHeadersData = await Promise.all(subCategoryPromises);

      // Safety net — backfill any examType/subExam combo present in `results`
      // that the criteria API didn't return a column for, so no mark is ever
      // silently dropped.
      const existingCombos = new Set();
      examHeadersData.forEach((exam) => {
        exam.subCategories.forEach((sub) => {
          existingCombos.add(`${exam.examType}|||${sub.name}`);
        });
      });

      results.forEach((r) => {
        if (r.subjectId === 0) return;
        const examTypeKey = normKey(r.examType);
        const subExamKey = normKey(r.subExam);
        const comboKey = `${examTypeKey}|||${subExamKey}`;
        if (existingCombos.has(comboKey)) return;

        existingCombos.add(comboKey);
        let examEntry = examHeadersData.find((e) => e.examType === examTypeKey);
        if (!examEntry) {
          examEntry = { examType: examTypeKey, examCategoryId: r.examTypeId, subCategories: [] };
          examHeadersData.push(examEntry);
        }
        examEntry.subCategories.push({
          name: subExamKey,
          maxMarks: r.maxMarks ?? "",
          minMarks: r.minMarks ?? "",
        });
      });

      const marksData = {};
      results.forEach((r) => {
        if (r.subjectId !== 0 && r.subjectName) {
          const subjectKey = sanitizeName(r.subjectName);
          const examTypeKey = normKey(r.examType);
          const subExamKey = normKey(r.subExam);

          if (!marksData[subjectKey]) marksData[subjectKey] = {};
          if (!marksData[subjectKey][examTypeKey]) marksData[subjectKey][examTypeKey] = {};
          marksData[subjectKey][examTypeKey][subExamKey] = r.obtainedMarks;
        }
      });

      const subjectTotalsMap = {};
      subjectTotals.forEach((s) => {
        subjectTotalsMap[s.subjectName] = s;
      });

      const updatedStudentProfile = {
        ...profile,
        teacherRemarks,
        grade: overallGrade,
        totalPercentage,
        resultStatus,
      };

      const selectedSession = sessions.find((s) => s.financialYearID === session);
      const sessionYear = selectedSession?.finanacialYear ?? "N/A";

      setLoadingBarProgress(100);

      handlePrint(
        updatedStudentProfile,
        attendance,
        subjectNames,
        examHeadersData,
        marksData,
        subjectTotalsMap,
        sessionYear,
        examWiseTotals
      );
    } catch (error) {
      setIsPdfLoading(false);
      setLoadingBarProgress(0);
      console.error("API request error:", error);
      setSnackbarMessage("Error generating result. Please try again.");
      setOpenSnackbar(true);
    }
  };

  // ─── PDF GENERATION — matches the redesigned staff-panel report card ──────
  const handlePrint = async (
    student,
    attendance,
    subjectNames,
    examHeadersData,
    marksData,
    subjectTotalsMap,
    sessionYear,
    examWiseTotals
  ) => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth(); // 297
      const pageHeight = doc.internal.pageSize.getHeight(); // 210

      const NAVY = [11, 31, 64];
      const TEAL = [0, 128, 128];
      const LIGHT_BG = [245, 247, 250];

      // Outer border
      const borderMargin = 8;
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.6);
      doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);

      // Thin inner accent border
      doc.setDrawColor(190, 171, 108);
      doc.setLineWidth(0.2);
      doc.rect(
        borderMargin + 1.5,
        borderMargin + 1.5,
        pageWidth - 2 * (borderMargin + 1.5),
        pageHeight - 2 * (borderMargin + 1.5)
      );

      // ── Logo & School Header ────────────────────────────────────────────
      try {
        doc.setFillColor(255, 255, 255);
        doc.rect(12, 16, 22, 22, "F");

        const schoolResponse = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({}),
        });

        let address = sessionStorage.getItem("address") || "N/A";
        let phoneNo = sessionStorage.getItem("phoneNo") || "N/A";
        let schoolName = sessionStorage.getItem("organizationName") || "N/A";

        if (schoolResponse.ok) {
          const apiData = await schoolResponse.json();
          address = apiData?.[0]?.address ?? address;
          phoneNo = apiData?.[0]?.phoneNo ?? phoneNo;
          schoolName = apiData?.[0]?.schoolName ?? schoolName;

          const logoImg = apiData?.[0]?.logoImg;
          if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
            let cleanedBase64 = logoImg.replace(/^data:image\/[a-zA-Z]+;base64,/, "").replace(/\s|\n/g, "");
            cleanedBase64 += "=".repeat((4 - (cleanedBase64.length % 4)) % 4);
            if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
              const logoUrl = `data:image/png;base64,${cleanedBase64}`;
              const apiImg = new Image();
              await new Promise((res, rej) => {
                apiImg.onload = res;
                apiImg.onerror = () => rej(new Error("Failed to load logo"));
                apiImg.src = logoUrl;
              });
              doc.setFillColor(255, 255, 255);
              doc.rect(12, 16, 22, 22, "F");
              doc.addImage(logoUrl, "PNG", 13.5, 18, 19, 19);
            }
          }
        }

        doc.setFont("times", "bold");
        doc.setFontSize(19);
        doc.text(schoolName.replace(/['"]+/g, ""), pageWidth / 2, 21, null, null, "center");

        doc.setFontSize(9.5);
        doc.setFont("times", "normal");
        doc.setTextColor(80, 80, 80);
        doc.text(address.replace(/['"]+/g, ""), pageWidth / 2, 28, null, null, "center");
        doc.text("Contact No.: " + phoneNo.replace(/['"]+/g, ""), pageWidth / 2, 34, null, null, "center");
        doc.setTextColor(0, 0, 0);
      } catch (err) {
        console.error("Logo/school header error:", err.message);
      }

      // PASS / FAIL stamp — top right
      const isPass = (student.resultStatus ?? "").toUpperCase() !== "FAIL";
      const stampColor = isPass ? [22, 130, 60] : [178, 34, 34];
      doc.setFillColor(...stampColor);
      doc.roundedRect(pageWidth - 46, 13, 30, 11, 2, 2, "F");
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(isPass ? "PASS" : "FAIL", pageWidth - 31, 20.5, null, null, "center");
      doc.setTextColor(0, 0, 0);

      // Divider
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.9);
      doc.line(15, 40, pageWidth - 15, 40);

      // Report title + student name
      doc.setFontSize(11.5);
      doc.setFont("times", "bold");
      doc.text(`Progress Report ${sessionYear}`, pageWidth / 2, 46, null, null, "center");

      doc.setFontSize(14);
      doc.text(`${student.studentName ?? "N/A"}`, pageWidth / 2, 54, null, null, "center");
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - 35, 57, pageWidth / 2 + 35, 57);

      // ── Student details panel ────────────────────────────────────────────
      const panelY = 61;
      const panelH = 24;
      doc.setFillColor(...LIGHT_BG);
      doc.roundedRect(15, panelY, pageWidth - 30, panelH, 2, 2, "F");

      doc.setFont("times", "normal");
      doc.setFontSize(9.5);
      const labelWidth = 30;
      const colLeft = 20;
      const colRight = pageWidth / 2 + 5;

      const leftDetails = [
        ["Father's Name:", student.fathersName ?? "N/A"],
        ["Mother's Name:", student.mothersName ?? "N/A"],
      ];
      const rightDetails = [
        ["Class & Section:", `${student.className ?? "N/A"} - ${student.sectionName ?? "N/A"}`],
        ["Roll No.:", String(student.rollNo ?? "N/A")],
      ];
      const thirdCol = [
        ["Admission No.:", String(student.admissionNo ?? "N/A")],
        ["Date of Birth:", student.studentDOB ?? "N/A"],
      ];

      const lineH = 8;
      let y1 = panelY + 8;
      leftDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colLeft, y1);
        doc.setFont("times", "normal");
        doc.text(String(value), colLeft + labelWidth, y1);
        y1 += lineH;
      });

      let y2 = panelY + 8;
      rightDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colRight, y2);
        doc.setFont("times", "normal");
        doc.text(String(value), colRight + labelWidth, y2);
        y2 += lineH;
      });

      const colThird = pageWidth - 95;
      let y3 = panelY + 8;
      thirdCol.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colThird, y3);
        doc.setFont("times", "normal");
        doc.text(String(value), colThird + labelWidth, y3);
        y3 += lineH;
      });

      // ── Marks Table ───────────────────────────────────────────────────────
      let lastY = panelY + panelH + 5;
      const minMargin = 15;

      if (examHeadersData.length > 0 && subjectNames.length > 0) {
        const headerRow1 = [
          { content: "S.No", rowSpan: 2, styles: { halign: "center" } },
          { content: "Subject", rowSpan: 2, styles: { halign: "center" } },
        ];
        const headerRow2 = [];

        examHeadersData.forEach((exam) => {
          headerRow1.push({
            content: exam.examType,
            colSpan: exam.subCategories.length,
            styles: { halign: "center" },
          });
          exam.subCategories.forEach((sub) => {
            const label = sub.name ? sub.name : "Marks";
            headerRow2.push({
              content: `${label}\n(Max:${sub.maxMarks})`,
              styles: { halign: "center" },
            });
          });
        });

        headerRow1.push(
          { content: "Total\n(Obt/Max)", rowSpan: 2, styles: { halign: "center" } },
          { content: "Grade", rowSpan: 2, styles: { halign: "center" } }
        );

        const tableHeaders = [headerRow1, headerRow2];

        const tableData = subjectNames.map((subject, idx) => {
          const row = [idx + 1, subject];

          examHeadersData.forEach((exam) => {
            exam.subCategories.forEach((sub) => {
              const mark = marksData[subject]?.[exam.examType]?.[sub.name];
              row.push(formatMarks(mark));
            });
          });

          const totals = subjectTotalsMap[subject];
          row.push(`${formatMarks(totals?.totalObtainedMarks)} / ${formatMarks(totals?.totalMaxMarks)}`);
          row.push(totals?.totalGrade ?? "--");

          return row;
        });

        const totalColumns =
          2 + examHeadersData.reduce((sum, e) => sum + e.subCategories.length, 0) + 2;
        const gradeColIndex = totalColumns - 1;

        const snoColW = 10;
        const subjectColW = 30;
        const totalColW = 24;
        const gradeColW = 16;
        const available = pageWidth - 2 * minMargin - snoColW - subjectColW - totalColW - gradeColW;
        const subExamColW = Math.min(20, available / (totalColumns - 4));
        const tableWidth = snoColW + subjectColW + (totalColumns - 4) * subExamColW + totalColW + gradeColW;
        const marginLeft = (pageWidth - tableWidth) / 2;

        const columnStyles = {
          0: { cellWidth: snoColW },
          1: { cellWidth: subjectColW },
        };
        let colIdx = 2;
        examHeadersData.forEach((exam) => {
          exam.subCategories.forEach(() => {
            columnStyles[colIdx++] = { cellWidth: subExamColW };
          });
        });
        columnStyles[colIdx] = { cellWidth: totalColW };
        columnStyles[colIdx + 1] = { cellWidth: gradeColW };

        doc.autoTable({
          startY: lastY,
          head: tableHeaders,
          body: tableData,
          styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 1.6 },
          headStyles: {
            fillColor: NAVY,
            textColor: [255, 255, 255],
            lineWidth: 0.5,
            font: "times",
            fontStyle: "bold",
            cellPadding: 1.6,
          },
          alternateRowStyles: { fillColor: LIGHT_BG },
          columnStyles,
          theme: "grid",
          margin: { left: marginLeft, right: marginLeft },
          tableWidth,
          didParseCell: (data) => {
            if (data.section === "head" && data.row.index === 1) {
              data.cell.styles.fillColor = TEAL;
            }
            if (data.section === "body" && data.column.index === gradeColIndex) {
              data.cell.styles.textColor = gradeColor(data.cell.raw);
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        lastY = doc.lastAutoTable.finalY + 5;
      }

      // ── Remarks + Overall Grade ──────────────────────────────────────────
      doc.setFontSize(9.5);
      doc.setFont("times", "bold");
      doc.text("Remarks:", 15, lastY);
      doc.setFont("times", "normal");
      doc.text(`${student.teacherRemarks ?? "N/A"}`, 33, lastY);

      const gradeText = `${student.grade ?? "N/A"} (${formatPercent(student.totalPercentage)})`;
      const rightMargin = 15;
      doc.setFont("times", "bold");
      doc.text("Overall Grade:", pageWidth - rightMargin - doc.getTextWidth(gradeText) - 32, lastY);
      doc.setFont("times", "normal");
      doc.setTextColor(...gradeColor(student.grade));
      doc.text(gradeText, pageWidth - rightMargin, lastY, null, null, "right");
      doc.setTextColor(0, 0, 0);

      lastY += 6;

      // ── Exam-wise totals strip ───────────────────────────────────────────
      if (examWiseTotals?.length > 0) {
        doc.setFillColor(...LIGHT_BG);
        doc.roundedRect(15, lastY - 4, pageWidth - 30, 8, 1.5, 1.5, "F");
        doc.setFontSize(8.5);
        doc.setFont("times", "bold");
        const summaryText = examWiseTotals
          .map(
            (e) =>
              `${e.examType}:  ${formatMarks(e.obtained)}/${formatMarks(e.max)}  (${formatPercent(
                e.percentage
              )})`
          )
          .join("        |        ");
        doc.text(summaryText, pageWidth / 2, lastY + 1, null, null, "center");
        doc.setFont("times", "normal");
        lastY += 10;
      } else {
        lastY += 4;
      }

      // ── Attendance + Grading Scale side by side ──────────────────────────
      const sideBySideY = lastY;
      const totalWidth = pageWidth - 2 * minMargin;
      const gap = 6;
      const attendanceWidth = totalWidth * 0.42;
      const gradingWidth = totalWidth - attendanceWidth - gap;
      const attendanceLeft = minMargin;
      const gradingLeft = minMargin + attendanceWidth + gap;

      const attendanceHeaders = [
        [
          { content: "Total Days", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Present", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Absent", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Attendance %", styles: { halign: "center", font: "times", fontStyle: "bold" } },
        ],
      ];
      const attendanceData = [
        [
          attendance?.totalDays ?? "N/A",
          attendance?.presentCount ?? "N/A",
          attendance?.absentCount ?? "N/A",
          attendance?.attendancePercentage != null ? formatPercent(attendance.attendancePercentage) : "N/A",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: attendanceHeaders,
        body: attendanceData,
        styles: { fontSize: 8, halign: "center", font: "times", cellPadding: 2 },
        headStyles: { fillColor: NAVY, textColor: [255, 255, 255], font: "times", fontStyle: "bold" },
        theme: "grid",
        margin: { left: attendanceLeft },
        tableWidth: attendanceWidth,
      });
      const attendanceFinalY = doc.lastAutoTable.finalY;

      const gradingHeaders = [
        [
          {
            content: "Marks %",
            styles: { halign: "center", fillColor: NAVY, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5, font: "times" },
          },
          "91-100", "81-90", "71-80", "61-70", "51-60", "41-50", "32-40", "Below 32",
        ],
      ];
      const gradingData = [
        [
          { content: "Grade", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold", fontSize: 7.5, font: "times" } },
          "A+", "A", "B+", "B", "C+", "C", "D", "E",
        ],
      ];

      doc.autoTable({
        startY: sideBySideY,
        head: gradingHeaders,
        body: gradingData,
        styles: { fontSize: 7.5, halign: "center", cellPadding: 2, font: "times" },
        theme: "grid",
        headStyles: { fillColor: TEAL, textColor: [255, 255, 255], font: "times" },
        tableWidth: gradingWidth,
        margin: { left: gradingLeft },
      });
      const gradingFinalY = doc.lastAutoTable.finalY;

      lastY = Math.max(attendanceFinalY, gradingFinalY) + 12;

      // ── Signature Lines & Footer ─────────────────────────────────────────
      doc.setFont("times", "normal");
      doc.setFontSize(9.5);

      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.3);
      doc.line(15, lastY, 75, lastY);
      doc.text("Signature of Class Teacher", 18, lastY + 5);

      doc.line(pageWidth - 75, lastY, pageWidth - 15, lastY);
      doc.text("Signature of Principal", pageWidth - 18, lastY + 5, null, null, "right");

      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth / 2,
        pageHeight - borderMargin - 3,
        null,
        null,
        "center"
      );
      doc.setTextColor(0, 0, 0);

      setIsPdfLoading(false);
      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (error) {
      setIsPdfLoading(false);
      console.error("Error generating PDF:", error);
      setSnackbarMessage("Error generating PDF: " + error.message);
      setOpenSnackbar(true);
    }
  };

  // useEffect
  useEffect(() => {
    fetchSessions();
    fetchSchoolLogo();
  }, []);

  useEffect(() => {
    if (session && classId && sectionId) {
      fetchResultData();
    }
  }, [session, classId, sectionId]);

  // Render
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <LoadingBar progress={loadingBarProgress} color="rgb(75, 108, 183)" height={5} />
      <Card sx={{ maxWidth: "100%", mx: "auto", boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <SchoolIcon sx={{ fontSize: 40, color: "rgba(75,108,183,1)", mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgba(49, 70, 119, 1)" }}>
              Student Exam Result
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={session}
              onChange={(e) => setSession(e.target.value)}
              displayEmpty
              sx={{ height: 45 }}
            >
              <MenuItem value="" disabled>
                Select Session
              </MenuItem>
              {sessions.map((item) => (
                <MenuItem key={item.financialYearID} value={item.financialYearID}>
                  {item.finanacialYear}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {resultData.length > 0 ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                Result Data
              </Typography>
              <Table sx={{ border: "1px solid #ccc", minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)" }}>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Roll No</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Student Name</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Total Marks</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Obtained Marks</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Grade</TableCell>
                    <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultData.map((student, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                        "&:hover": { backgroundColor: "#f0f0f0" },
                      }}
                    >
                      <TableCell align="center">{student.rollNo}</TableCell>
                      <TableCell align="center">{student.studentName}</TableCell>
                      <TableCell align="center">{student.totalMaxMarks}</TableCell>
                      <TableCell align="center">{student.totalObtainedMarks}</TableCell>
                      <TableCell align="center">{student.grade}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Print Result">
                          <span>
                            <Button
                              onClick={() => {
                                if (student && student.studentId) {
                                  handlePrintClick(student.studentId);
                                } else {
                                  setSnackbarMessage("Student data not available");
                                  setOpenSnackbar(true);
                                }
                              }}
                              variant="contained"
                              disabled={isFeeCheckLoading}
                              sx={{
                                backgroundColor: "rgba(75,108,183,1)",
                                "&:hover": { backgroundColor: "rgba(24,40,72,1)" },
                                minWidth: "72px",
                              }}
                            >
                              {isFeeCheckLoading ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                              ) : (
                                "Print"
                              )}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
              No result data available.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Fee-due block — no bypass for parents ── */}
      <Dialog
        open={dueDialogOpen}
        onClose={handleCloseDueDialog}
        maxWidth="sm"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#b22222",
            color: "#fff",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon /> Result Unavailable
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="body1" sx={{color:'red', fontWeight:'bold', mt: 1, mb: 1 }}>
            {dueData?.message || "The result cannot be generated because there are unpaid fee dues."}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Total Due Amount: ₹{dueData?.totalDueAmount ?? 0}
          </Typography>

          {dueData?.dueMonths?.length > 0 && (
            <Table size="small" sx={{ border: "1px solid #eee" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total Fee</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Payment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dueData.dueMonths.map((m, idx) => {
                  const isPaid = m.paidStatus === 1;
                  return (
                    <TableRow key={`${m.monthId}-${idx}`}>
                      <TableCell>{MONTH_NAMES[m.monthId] ?? m.monthId}</TableCell>
                      <TableCell align="right">{m.totalFee}</TableCell>
                      <TableCell align="right">{m.paidAmount}</TableCell>
                      <TableCell align="right">{m.balance}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isPaid ? "Paid" : "Unpaid"}
                          size="small"
                          color={isPaid ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="center">{formatDueDate(m.feePaymentDate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            Please clear the pending dues to view or download the result.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleCloseDueDialog} variant="contained" color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PDF generation progress ── */}
      <Dialog
        open={isPdfLoading}
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
      >
        <DialogContent sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}>
          <CircularProgress size={32} sx={{ mb: 1.5 }} />
          <Typography>Generating PDF, please wait...</Typography>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Result;